import React, { useState, useMemo, useEffect } from 'react';
import { fetchWithAuth } from './lib/api';
import { useBilling } from './hooks/useBilling';
import UpgradeModal from './components/UpgradeModal';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Paper,
  TextField,
  IconButton,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Badge,
  Avatar,
  Container,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Save, Close, Add, ExpandMore, ExpandLess, 
  Payment as PaymentIcon, History as HistoryIcon,
  CheckCircle, Cancel, HourglassEmpty, Delete,
  Schedule as ScheduleIcon,
  AccountBalanceWallet,
  People,
  ChevronLeft,
  ChevronRight,
  TrendingUp
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/el';

// React Query imports
import { useQueryClient } from '@tanstack/react-query';
import { useSalaries } from './hooks/useSalaries';
import { useCumulativePayments, useEmployeePayments, useCreatePayment, useDeletePayment } from './hooks/useEmployeePayments';
import { useShifts, useUpdateShift, useDeleteShift, useCreateShift } from './hooks/useShifts';
import { useUpdateEmployee } from './hooks/useEmployees';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';
import { AccessDenied } from './components/PermissionGate';
import EmployeeManagementModal from './components/EmployeeManagementModal';
import { usePositions } from './PositionsContext';
import { usePresetHours } from './PresetHoursContext';

dayjs.extend(isoWeek);
dayjs.locale('el');

const formatWeekBalanceLabel = (displayBalance, balanceKind) => {
  const amount = Math.abs(displayBalance || 0);
  if (balanceKind === 'prepaid' || displayBalance < -0.01) {
    return { text: `-€${amount.toFixed(2)}`, color: '#16a34a', caption: 'Προκαταβολή' };
  }
  if (balanceKind === 'debt' || displayBalance > 0.01) {
    return { text: `€${amount.toFixed(2)}`, color: '#dc2626', caption: 'Υπόλοιπο' };
  }
  return { text: '€0.00', color: '#16a34a', caption: 'Υπόλοιπο' };
};

const PaymentManagementV2 = () => {
  // Theme and responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Permission checks
  const { can } = usePermissions();
  const canView = can(PERMISSIONS.SALARIES_VIEW);
  const canCreate = can(PERMISSIONS.SALARIES_CREATE);
  const canEdit = can(PERMISSIONS.SALARIES_EDIT);
  const { hasFeature } = useBilling();
  const [exportUpgradeOpen, setExportUpgradeOpen] = useState(false);
  // Query client for invalidation
  const queryClient = useQueryClient();
  
  // Get positions and preset hours from context
  const { positions } = usePositions();
  const { presetHours } = usePresetHours();
  
  // Get week start (Monday) using ISO week - returns dayjs object
  const getWeekStart = (date) => {
    return dayjs(date).startOf('isoWeek');
  };
  
  // Initialize with the current week
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filter, setFilter] = useState('all'); // all, paid, unpaid, partial
  const [searchTerm, setSearchTerm] = useState(''); // Search by name
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [dismissedError, setDismissedError] = useState(false);
  
  // State for history modal employee selection
  const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
  
  // State for employee management modal
  const [employeeModal, setEmployeeModal] = useState({ open: false, emp: null, tab: 2 }); // tab 2 = payment history
  const [editForm, setEditForm] = useState({});
  const [fixedSchedule, setFixedSchedule] = useState({});
  const [savingFixedSchedule, setSavingFixedSchedule] = useState(false);
  
  // React Query hooks
  const weekStartStr = selectedWeek.format('YYYY-MM-DD');
  const { data: salariesData = [], isLoading: salariesLoading, error: salariesError } = useSalaries(weekStartStr);
  const { data: cumulativeData = [], isLoading: cumulativeLoading } = useCumulativePayments(weekStartStr);
  const { data: historyPayments = [], isLoading: historyLoading } = useEmployeePayments(historyEmployeeId);
  
  // Fetch shifts for selected employee in modal
  const modalEmployeeId = employeeModal.emp?.id;
  const { data: modalEmployeeShifts = [] } = useShifts(
    modalEmployeeId ? { employee_id: modalEmployeeId } : {}
  );
  
  // React Query mutations
  const createPaymentMutation = useCreatePayment();
  const deletePaymentMutation = useDeletePayment();
  const updateEmployeeMutation = useUpdateEmployee();
  
  // Computed loading and error states
  const loading = salariesLoading || cumulativeLoading;
  const error = !dismissedError && (salariesError?.message || '');

  useEffect(() => {
    setDismissedError(false);
  }, [weekStartStr]);
  
  // Computed employees data from React Query
  const employees = useMemo(() => {
    if (!salariesData || salariesData.length === 0) return [];
    
    // Create a map of cumulative data by employee id
    const cumulativeMap = {};
    if (Array.isArray(cumulativeData)) {
      cumulativeData.forEach(emp => {
        cumulativeMap[emp.id] = emp;
      });
    }
    
    // Enrich employee data with payment information from salaries API and cumulative data
    return salariesData.map(employee => {
      // For current week display - use salaries data
      const totalSalary = employee.totalSalary || employee.weeklySalary || 0;
      const totalPaid = employee.paidAmount || 0;

      // Get cumulative data for this employee (includes credit carry-forward from overpayments)
      const cumulativeInfo = cumulativeMap[employee.id] || {};
      const currentWeekInfo = cumulativeInfo.current_week;
      const unpaidWeeks = cumulativeInfo.unpaid_weeks || [];
      const creditBalance = cumulativeInfo.credit_balance || 0;

      // Adjusted unpaid: prior overpayments reduce debt on later weeks
      const rawUnpaid = totalSalary - totalPaid;
      const unpaidAmount =
        currentWeekInfo?.unpaid !== undefined && currentWeekInfo?.unpaid !== null
          ? currentWeekInfo.unpaid
          : Math.max(0, rawUnpaid);

      const cumulativeOwed =
        cumulativeInfo.cumulative_owed !== undefined && cumulativeInfo.cumulative_owed !== null
          ? cumulativeInfo.cumulative_owed
          : Math.max(0, unpaidAmount);

      const displayBalance =
        cumulativeInfo.display_balance !== undefined && cumulativeInfo.display_balance !== null
          ? cumulativeInfo.display_balance
          : unpaidAmount;
      const balanceKind =
        cumulativeInfo.balance_kind ||
        (displayBalance < -0.01 ? 'prepaid' : displayBalance > 0.01 ? 'debt' : 'zero');

      let paymentStatus = 'unpaid';
      if (balanceKind === 'prepaid') {
        paymentStatus = 'prepaid';
      } else if (totalSalary > 0) {
        if (unpaidAmount <= 0.01) {
          paymentStatus = 'paid';
        } else if (totalPaid > 0 || (currentWeekInfo?.credit_applied || 0) > 0) {
          paymentStatus = 'partial';
        }
      }
      
      return {
        id: employee.id,
        name: employee.name,
        first_name: employee.first_name,
        last_name: employee.last_name,
        position: employee.position || '',
        hourly_wage: employee.hourly_wage || 0,
        saturday_wage: employee.saturday_wage || 0,
        sunday_wage: employee.sunday_wage || 0,
        shift_preference: employee.shift_preference || 'any',
        max_days_per_week: employee.max_days_per_week || 7,
        available_days: employee.available_days || '0,1,2,3,4,5,6',
        shift_preferences: employee.shift_preferences || 'any',
        notes: employee.notes || '',
        active: employee.active !== undefined ? employee.active : 1,
        shifts: [], // Shifts not needed for display anymore
        totalSalary: Math.round((totalSalary || 0) * 100) / 100,
        totalHours: employee.totalHours || 0,
        totalPaid: Math.round((totalPaid || 0) * 100) / 100,
        unpaidAmount: Math.round(unpaidAmount * 100) / 100,
        displayBalance: Math.round(displayBalance * 100) / 100,
        balanceKind,
        cumulativeOwed: Math.round(cumulativeOwed * 100) / 100,
        creditBalance: Math.round(creditBalance * 100) / 100,
        creditApplied: Math.round((currentWeekInfo?.credit_applied || 0) * 100) / 100,
        unpaidWeeks: unpaidWeeks,
        paymentStatus
      };
    });
  }, [salariesData, cumulativeData]);
  
  // Update historyEmployeeId and editForm when employee modal opens
  useEffect(() => {
    if (employeeModal.open && employeeModal.emp) {
      const emp = employeeModal.emp;
      setHistoryEmployeeId(emp.id);
      // Populate editForm with employee data
      setEditForm({
        name: emp.name || '',
        position: emp.position || '',
        hourly_wage: emp.hourly_wage || '',
        saturday_wage: emp.saturday_wage || '',
        sunday_wage: emp.sunday_wage || '',
        shift_preference: emp.shift_preference || '',
        max_days_per_week: emp.max_days_per_week || 7,
        available_days: emp.available_days || '0,1,2,3,4,5,6',
        shift_preferences: emp.shift_preferences || 'any',
        notes: emp.notes || ''
      });
    }
  }, [employeeModal.open, employeeModal.emp]);
  
  // Calculate payment stats for modal employee
  const modalPaymentStats = useMemo(() => {
    if (!modalEmployeeId || !modalEmployeeShifts || modalEmployeeShifts.length === 0) {
      return { totalSalary: 0, totalPaid: 0, unpaid: 0 };
    }
    
    // Calculate total salary from shifts
    let totalSalary = 0;
    const employee = employeeModal.emp;
    
    modalEmployeeShifts.forEach(shift => {
      const startParts = shift.start_time.split(':');
      const endParts = shift.end_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }
      const hours = (endMinutes - startMinutes) / 60;
      
      if (hours > 0) {
        // Use stored wage from shift or employee wage
        const wage = shift.hourly_wage || employee?.hourly_wage || 0;
        totalSalary += hours * wage;
      }
    });
    
    // Calculate total paid from history payments
    const totalPaid = historyPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    
    return {
      totalSalary: Math.round(totalSalary * 100) / 100,
      totalPaid: Math.round(totalPaid * 100) / 100,
      unpaid: Math.round((totalSalary - totalPaid) * 100) / 100
    };
  }, [modalEmployeeId, modalEmployeeShifts, employeeModal.emp, historyPayments]);
  
  // Payment modal state
  const [paymentModal, setPaymentModal] = useState({ 
    open: false, 
    employee: null,
    amount: '',
    paymentMethod: 'cash',
    reference: '',
    notes: '',
    paymentDate: dayjs()
  });
  
  // History modal state
  const [historyModal, setHistoryModal] = useState({
    open: false,
    employee: null,
    payments: []
  });
  
  // Editing shift states
  const [editingShift, setEditingShift] = useState(null);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editDay, setEditDay] = useState("");
  
  // Shifts modal state
  const [shiftsModal, setShiftsModal] = useState({
    open: false,
    employee: null
  });
  
  // Shifts form state - track times for each day
  const [shiftsDayTimes, setShiftsDayTimes] = useState({});
  
  // Days of week constant
  const DAYS_OF_WEEK = [
    { value: 1, label: 'Δευτέρα' },
    { value: 2, label: 'Τρίτη' },
    { value: 3, label: 'Τετάρτη' },
    { value: 4, label: 'Πέμπτη' },
    { value: 5, label: 'Παρασκευή' },
    { value: 6, label: 'Σάββατο' },
    { value: 0, label: 'Κυριακή' }
  ];
  
  // Preset hours for quick selection (use context or fallback)
  const presetHoursOptions = presetHours || ['07:00-15:00', '09:00-17:00', '15:00-23:00', '17:00-01:00', '23:00-07:00'];
  
  // Shifts query - only fetch when modal is open
  const { data: employeeShifts = [], isLoading: shiftsLoading } = useShifts(
    shiftsModal.employee ? { week_start: weekStartStr, employee_id: shiftsModal.employee.id } : {}
  );
  
  // Shift mutations
  const updateShiftMutation = useUpdateShift();
  const deleteShiftMutation = useDeleteShift();
  const createShiftMutation = useCreateShift();

  // Format functions
  const formatDay = (dayNumber) => {
    const days = ['Κυριακή', 'Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο'];
    return days[parseInt(dayNumber)] || 'Unknown';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const formatDateForAPI = (date) => {
    // Handle both Date objects and dayjs objects
    if (dayjs.isDayjs(date)) {
      return date.format('YYYY-MM-DD');
    }
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return dayjs(date).format('YYYY-MM-DD');
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('el-GR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };


  // Open payment modal
  const handleOpenPaymentModal = (employee) => {
    setPaymentModal({
      open: true,
      employee,
      amount: employee.unpaidAmount > 0 ? employee.unpaidAmount.toString() : '',
      paymentMethod: 'cash',
      reference: '',
      notes: '',
      paymentDate: dayjs()
    });
  };

  // Close payment modal
  const handleClosePaymentModal = () => {
    setPaymentModal({ 
      open: false, 
      employee: null,
      amount: '',
      paymentMethod: 'cash',
      reference: '',
      notes: '',
      paymentDate: dayjs()
    });
  };

  // Submit payment
  const handleSubmitPayment = async () => {
    const { employee, amount, paymentMethod, reference, notes, paymentDate } = paymentModal;
    
    if (!employee || !amount || parseFloat(amount) <= 0) {
      setSnackbar({
        open: true,
        message: 'Παρακαλώ εισάγετε έγκυρο ποσό πληρωμής',
        severity: 'error'
      });
      return;
    }

    try {
      const paymentDateStr = formatDateForAPI(paymentDate);
      
      const paymentData = {
        employee_id: employee.id,
        payment_date: paymentDateStr,
        amount: parseFloat(amount),
        payment_method: paymentMethod,
        reference,
        notes
      };

      // Create new payment with automatic allocation to oldest unpaid weeks first (FIFO)
      let remainingAmount = parseFloat(amount);
      const allocations = [];
      
      // Sort unpaid weeks by date (oldest first)
      const sortedUnpaidWeeks = [...(employee.unpaidWeeks || [])].sort((a, b) => 
        new Date(a.week_start) - new Date(b.week_start)
      );
      
      // Allocate payment starting from oldest week
      for (const week of sortedUnpaidWeeks) {
        if (remainingAmount <= 0) break;
        
        const weekUnpaid = week.unpaid;
        const allocationAmount = Math.min(remainingAmount, weekUnpaid);
        
        allocations.push({
          week_start: week.week_start,
          allocated_amount: allocationAmount
        });
        
        remainingAmount -= allocationAmount;
      }
      
      // If there's still remaining amount (overpayment or no unpaid weeks), allocate to current week
      if (remainingAmount > 0 || allocations.length === 0) {
        const currentWeekStartStr = formatDateForAPI(selectedWeek);
        const amountToAllocate = remainingAmount > 0 ? remainingAmount : parseFloat(amount);
        const existingAlloc = allocations.find(a => a.week_start === currentWeekStartStr);
        if (existingAlloc) {
          existingAlloc.allocated_amount += amountToAllocate;
        } else {
          allocations.push({
            week_start: currentWeekStartStr,
            allocated_amount: amountToAllocate
          });
        }
      }
      
      paymentData.allocations = allocations;
      await createPaymentMutation.mutateAsync(paymentData);

      setSnackbar({
        open: true,
        message: `Η πληρωμή €${amount} για τον/την ${employee.name} καταχωρήθηκε επιτυχώς`,
        severity: 'success'
      });

      handleClosePaymentModal();
      // Data will be automatically refetched by React Query invalidation
    } catch (error) {
      console.error('🔴 Error saving payment:', error);
      console.error('🔴 Error details:', error.message, error.response);
      setSnackbar({
        open: true,
        message: `Σφάλμα κατά την αποθήκευση της πληρωμής: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  // Open history modal
  const handleOpenHistoryModal = (employee) => {
    // Set the employee ID to trigger React Query fetch
    setHistoryEmployeeId(employee.id);
    setHistoryModal({
      open: true,
      employee,
      payments: [] // Will be populated by React Query
    });
  };

  // Close history modal
  const handleCloseHistoryModal = () => {
    setHistoryEmployeeId(null);
    setHistoryModal({
      open: false,
      employee: null,
      payments: []
    });
  };

  // Delete payment
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) {
      return;
    }

    try {
      await deletePaymentMutation.mutateAsync(paymentId);

      setSnackbar({
        open: true,
        message: 'Η πληρωμή διαγράφηκε επιτυχώς',
        severity: 'success'
      });

      // Data will be automatically refetched by React Query invalidation
    } catch (error) {
      console.error('Error deleting payment:', error);
      setSnackbar({
        open: true,
        message: 'Σφάλμα κατά τη διαγραφή της πληρωμής',
        severity: 'error'
      });
    }
  };

  // Handle employee edit save (basic info)
  const handleEditSave = async () => {
    const emp = employeeModal.emp;
    
    if (!emp) {
      setSnackbar({ open: true, message: 'Σφάλμα: Δεν βρέθηκε ο υπάλληλος', severity: 'error' });
      return;
    }
    
    const payload = {
      name: editForm.name || emp.name,
      position: editForm.position || emp.position,
      hourly_wage: parseFloat(editForm.hourly_wage) || 0,
      saturday_wage: editForm.saturday_wage ? parseFloat(editForm.saturday_wage) : null,
      sunday_wage: editForm.sunday_wage ? parseFloat(editForm.sunday_wage) : null,
      shift_preference: editForm.shift_preference || emp.shift_preference || 'any',
      max_days_per_week: emp.max_days_per_week || 7,
      available_days: emp.available_days || '0,1,2,3,4,5,6',
      shift_preferences: emp.shift_preferences || 'any',
      notes: emp.notes || '',
      active: emp.active !== undefined ? emp.active : 1
    };
    
    try {
      await updateEmployeeMutation.mutateAsync({ id: emp.id, data: payload });
      setSnackbar({ open: true, message: 'Υπάλληλος ενημερώθηκε επιτυχώς', severity: 'success' });
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      queryClient.invalidateQueries({ queryKey: ['payments', 'cumulative'] });
    } catch (error) {
      console.error('Error updating employee:', error);
      setSnackbar({ open: true, message: error.message || 'Σφάλμα ενημέρωσης υπαλλήλου', severity: 'error' });
    }
  };

  // Handle preferences save
  const handlePreferencesSave = async () => {
    const emp = employeeModal.emp;
    
    if (!emp) {
      setSnackbar({ open: true, message: 'Σφάλμα: Δεν βρέθηκε ο υπάλληλος', severity: 'error' });
      return;
    }
    
    const payload = {
      name: emp.name,
      position: emp.position,
      hourly_wage: emp.hourly_wage || 0,
      shift_preference: emp.shift_preference || 'any',
      max_days_per_week: editForm.max_days_per_week || 7,
      available_days: editForm.available_days || '0,1,2,3,4,5,6',
      shift_preferences: editForm.shift_preferences || 'any',
      notes: editForm.notes || '',
      active: emp.active !== undefined ? emp.active : 1
    };
    
    try {
      await updateEmployeeMutation.mutateAsync({ id: emp.id, data: payload });
      setSnackbar({ open: true, message: 'Προτιμήσεις ενημερώθηκαν επιτυχώς', severity: 'success' });
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
    } catch (error) {
      console.error('Error updating preferences:', error);
      setSnackbar({ open: true, message: 'Σφάλμα ενημέρωσης προτιμήσεων', severity: 'error' });
    }
  };

  // Expand/collapse toggle handler
  const handleExpandToggle = (employeeId) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };
  
  // Shifts modal handlers
  const handleOpenShiftsModal = (employee) => {
    // Initialize day times from existing shifts
    const dayTimes = {};
    DAYS_OF_WEEK.forEach(day => {
      dayTimes[day.value] = { startTime: '', endTime: '', shiftId: null };
    });
    
    setShiftsModal({
      open: true,
      employee
    });
    setShiftsDayTimes(dayTimes);
  };
  
  // Effect to populate shift times when shifts load
  React.useEffect(() => {
    if (shiftsModal.open && employeeShifts.length > 0) {
      const dayTimes = {};
      DAYS_OF_WEEK.forEach(day => {
        const shift = employeeShifts.find(s => parseInt(s.day) === day.value);
        if (shift) {
          dayTimes[day.value] = {
            startTime: shift.start_time?.substring(0, 5) || '',
            endTime: shift.end_time?.substring(0, 5) || '',
            shiftId: shift.id
          };
        } else {
          dayTimes[day.value] = { startTime: '', endTime: '', shiftId: null };
        }
      });
      setShiftsDayTimes(dayTimes);
    }
  }, [employeeShifts, shiftsModal.open]);
  
  const handleCloseShiftsModal = () => {
    setShiftsModal({
      open: false,
      employee: null
    });
    setShiftsDayTimes({});
  };
  
  const handleSaveAllShifts = async () => {
    if (!shiftsModal.employee) return;
    
    try {
      let changesCount = 0;
      
      for (const day of DAYS_OF_WEEK) {
        const dayTime = shiftsDayTimes[day.value];
        const existingShift = employeeShifts.find(s => parseInt(s.day) === day.value);
        
        if (dayTime.startTime && dayTime.endTime) {
          if (existingShift) {
            // Update existing shift if times changed
            if (existingShift.start_time !== dayTime.startTime || existingShift.end_time !== dayTime.endTime) {
              await updateShiftMutation.mutateAsync({
                id: existingShift.id,
                data: {
                  start_time: dayTime.startTime,
                  end_time: dayTime.endTime,
                  week_start: weekStartStr
                }
              });
              changesCount++;
            }
          } else {
            // Create new shift
            await createShiftMutation.mutateAsync({
              employee_id: shiftsModal.employee.id,
              day: day.value,
              start_time: dayTime.startTime,
              end_time: dayTime.endTime,
              week_start: weekStartStr
            });
            changesCount++;
          }
        } else if (existingShift && (!dayTime.startTime || !dayTime.endTime)) {
          // Delete shift if times are cleared
          await deleteShiftMutation.mutateAsync(existingShift.id);
          changesCount++;
        }
      }
      
      // Invalidate salaries and cumulative queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['salaries'] });
      queryClient.invalidateQueries({ queryKey: ['payments', 'cumulative'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      
      setSnackbar({
        open: true,
        message: changesCount > 0 ? `${changesCount} αλλαγές αποθηκεύτηκαν!` : 'Δεν υπήρχαν αλλαγές',
        severity: 'success'
      });
      
      handleCloseShiftsModal();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Σφάλμα: ${error.message}`,
        severity: 'error'
      });
    }
  };
  
  const handleApplyPreset = (dayValue, preset) => {
    const [startTime, endTime] = preset.split('-');
    setShiftsDayTimes(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], startTime, endTime }
    }));
  };

  // Smart time input formatting - auto adds ":" and formats properly
  const formatTimeInput = (value) => {
    const nums = value.replace(/\D/g, '').slice(0, 4); // max 4 digits
    
    if (nums.length === 0) return '';
    if (nums.length === 1) {
      // Single digit > 2 means it's the hour (e.g., 9 → 09:)
      return parseInt(nums) > 2 ? `0${nums}:` : nums;
    }
    if (nums.length === 2) return `${nums}:`;
    if (nums.length === 3) return `${nums.slice(0,2)}:${nums.slice(2)}`;
    return `${nums.slice(0,2)}:${nums.slice(2,4)}`;
  };

  const handleTimeChange = (dayValue, field, value) => {
    const formatted = formatTimeInput(value);
    setShiftsDayTimes(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], [field]: formatted }
    }));
  };
  
  const handleApplyToAllDays = (startTime, endTime) => {
    const newDayTimes = {};
    DAYS_OF_WEEK.forEach(day => {
      newDayTimes[day.value] = { 
        ...shiftsDayTimes[day.value],
        startTime, 
        endTime 
      };
    });
    setShiftsDayTimes(newDayTimes);
  };
  
  const handleClearDay = (dayValue) => {
    setShiftsDayTimes(prev => ({
      ...prev,
      [dayValue]: { ...prev[dayValue], startTime: '', endTime: '' }
    }));
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status, totalPaid, totalSalary, creditApplied = 0) => {
    const partialAmount = creditApplied > 0.01
      ? creditApplied
      : totalPaid;
    const configs = {
      paid: { icon: <CheckCircle sx={{ fontSize: 16 }} />, label: 'Πληρωμένο', color: 'success', bg: 'rgba(34,197,94,0.1)' },
      prepaid: { icon: <CheckCircle sx={{ fontSize: 16 }} />, label: 'Προκαταβολή', color: 'success', bg: 'rgba(34,197,94,0.1)' },
      partial: { icon: <HourglassEmpty sx={{ fontSize: 16 }} />, label: `Μερικώς (€${partialAmount.toFixed(2)})`, color: 'warning', bg: 'rgba(245,158,11,0.1)' },
      unpaid: { icon: <Cancel sx={{ fontSize: 16 }} />, label: 'Απλήρωτο', color: 'error', bg: 'rgba(239,68,68,0.1)' }
    };
    const config = configs[status] || configs.unpaid;
    return (
      <Chip 
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ 
          fontWeight: 600,
          borderRadius: '8px',
          '& .MuiChip-icon': { ml: 0.5 }
        }}
      />
    );
  };

  // Editing shift functions
  const handleStartEdit = (shift) => {
    setEditingShift(shift.id);
    setEditStartTime(shift.start_time);
    setEditEndTime(shift.end_time);
    setEditDay(shift.day);
  };

  const handleCancelEdit = () => {
    setEditingShift(null);
    setEditStartTime("");
    setEditEndTime("");
    setEditDay("");
  };

  const handleSaveEdit = async (shift) => {
    try {
      const weekStart = selectedWeek;
      const weekStartStr = formatDateForAPI(weekStart);
      
      const response = await fetchWithAuth(`/api/shifts/${shift.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...shift,
          start_time: editStartTime,
          end_time: editEndTime,
          day: editDay,
          week_start: weekStartStr
        })
      });

      if (!response.ok) throw new Error('Failed to update shift');

      setSnackbar({
        open: true,
        message: 'Η βάρδια ενημερώθηκε επιτυχώς',
        severity: 'success'
      });

      handleCancelEdit();
      fetchWeekData();
    } catch (error) {
      console.error('Error updating shift:', error);
      setSnackbar({
        open: true,
        message: 'Σφάλμα κατά την ενημέρωση της βάρδιας',
        severity: 'error'
      });
    }
  };

  // React Query automatically refetches when weekStartStr changes

  const filteredEmployees = employees.filter(employee => {
    // Filter by payment status
    if (filter === 'paid' && employee.paymentStatus !== 'paid') return false;
    if (filter === 'unpaid' && employee.paymentStatus !== 'unpaid') return false;
    if (filter === 'partial' && employee.paymentStatus !== 'partial') return false;
    
    // Filter by name search
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      const nameLower = (employee.name || '').toLowerCase();
      if (!nameLower.includes(searchLower)) return false;
    }
    
    return true;
  });

  const handleCheckboxChange = (employeeId) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };

  const totalSalaries = filteredEmployees.reduce((sum, emp) => sum + (emp.totalSalary || 0), 0);
  const totalPaid = filteredEmployees.reduce((sum, emp) => sum + (emp.totalPaid || 0), 0);
  const totalUnpaid = filteredEmployees.reduce((sum, emp) => sum + (emp.unpaidAmount || 0), 0);

  // Access denied if user can't view salaries
  if (!canView) {
    return <AccessDenied message="Δεν έχετε πρόσβαση στη διαχείριση πληρωμών." />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)'
            }}>
              <AccountBalanceWallet sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Διαχείριση Πληρωμών
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Εβδομαδιαίος έλεγχος μισθοδοσίας
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDismissedError(true)}>
            {error}
          </Alert>
        )}

        {/* Week Navigator */}
        <Paper sx={{ 
          p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(37,99,235,0.08) 100%)',
          border: '1px solid rgba(59,130,246,0.12)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, md: 2 } }}>
            <IconButton 
              onClick={() => setSelectedWeek(prev => getWeekStart(prev.subtract(7, 'day')))}
              sx={{ 
                bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: 'primary.main', color: 'white' },
                transition: 'all 0.2s'
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Box sx={{ 
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: { xs: 2, md: 3 }, py: 1,
              bgcolor: 'white', borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}>
              <ScheduleIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {selectedWeek.format('DD/MM')} — {selectedWeek.add(6, 'day').format('DD/MM/YYYY')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Εβδομάδα {selectedWeek.isoWeek()}
                </Typography>
              </Box>
              <DatePicker
                value={selectedWeek}
                onChange={(newDate) => setSelectedWeek(getWeekStart(newDate))}
                slotProps={{ 
                  textField: { 
                    size: 'small',
                    sx: { width: 0, minWidth: 0, overflow: 'hidden', '& fieldset': { border: 'none' } }
                  },
                  openPickerButton: {
                    sx: { ml: -1 }
                  }
                }}
              />
            </Box>
            <IconButton 
              onClick={() => setSelectedWeek(prev => getWeekStart(prev.add(7, 'day')))}
              sx={{ 
                bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': { bgcolor: 'primary.main', color: 'white' },
                transition: 'all 0.2s'
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Paper>

        {/* Summary Stats Cards */}
        <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(59, 130, 246, 0.4)' }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                      Συνολικός Μισθός
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                      €{totalSalaries.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Τρέχουσα εβδομάδα
                    </Typography>
                  </Box>
                  {!isMobile && <AccountBalanceWallet sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(34, 197, 94, 0.4)' }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                      Πληρωμένα
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                      €{totalPaid.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {filteredEmployees.filter(e => e.paymentStatus === 'paid').length} πληρωμένοι
                    </Typography>
                  </Box>
                  {!isMobile && <CheckCircle sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(239, 68, 68, 0.4)' }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                      Υπόλοιπο
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                      €{totalUnpaid.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {filteredEmployees.filter(e => e.paymentStatus === 'unpaid').length} απλήρωτοι
                    </Typography>
                  </Box>
                  {!isMobile && <HourglassEmpty sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)' }
            }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                      Υπάλληλοι
                    </Typography>
                    <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: '#fff', my: 0.5 }}>
                      {filteredEmployees.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      {filteredEmployees.filter(e => e.paymentStatus === 'partial').length} μερικώς
                    </Typography>
                  </Box>
                  {!isMobile && <People sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }} />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter & Export */}
        <Paper sx={{ 
          p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: { xs: 1.5, md: 2 } 
          }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, md: 2 }, 
              alignItems: { xs: 'stretch', sm: 'center' }, 
              flex: 1 
            }}>
              <TextField
                size="small"
                label="Αναζήτηση ονόματος"
                placeholder={isMobile ? "Όνομα..." : "Πληκτρολογήστε όνομα..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  minWidth: { xs: '100%', sm: 200, md: 250 },
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                {[
                  { value: 'all', label: 'Όλοι', count: employees.length },
                  { value: 'paid', label: 'Πληρωμένοι', count: employees.filter(e => e.paymentStatus === 'paid').length },
                  { value: 'partial', label: 'Μερικώς', count: employees.filter(e => e.paymentStatus === 'partial').length },
                  { value: 'unpaid', label: 'Απλήρωτοι', count: employees.filter(e => e.paymentStatus === 'unpaid').length }
                ].map(f => (
                  <Chip
                    key={f.value}
                    label={`${f.label} (${f.count})`}
                    onClick={() => setFilter(f.value)}
                    variant={filter === f.value ? 'filled' : 'outlined'}
                    color={
                      filter === f.value 
                        ? (f.value === 'paid' ? 'success' : f.value === 'unpaid' ? 'error' : f.value === 'partial' ? 'warning' : 'primary')
                        : 'default'
                    }
                    sx={{ 
                      fontWeight: filter === f.value ? 600 : 400,
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'translateY(-1px)' }
                    }}
                  />
                ))}
              </Stack>
            </Box>
            {!isMobile && (
              <Button 
                variant="outlined" 
                size="small"
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={() => {
                  if (!hasFeature('exportReports')) {
                    setExportUpgradeOpen(true);
                    return;
                  }
                  const weekStartStr = formatDateForAPI(selectedWeek);
                  const csvContent = [
                    ['Όνομα', 'Θέση', 'Ωρομίσθιο', 'Συνολικός Μισθός', 'Πληρωμένα', 'Υπόλοιπο Εβδομάδας', 'Υπόλοιπο', 'Κατάσταση'].join(','),
                    ...filteredEmployees.map(emp => [
                      emp.name,
                      emp.position || '',
                      (emp.hourly_wage || 0).toFixed(2),
                      (emp.totalSalary || 0).toFixed(2),
                      (emp.totalPaid || 0).toFixed(2),
                      (emp.displayBalance ?? emp.unpaidAmount ?? 0).toFixed(2),
                      (emp.cumulativeOwed || 0).toFixed(2),
                      emp.paymentStatus === 'prepaid' ? 'Προκαταβολή' : emp.paymentStatus === 'paid' ? 'Πληρωμένο' : emp.paymentStatus === 'partial' ? 'Μερικώς' : 'Απλήρωτο'
                    ].join(','))
                  ].join('\n');
                  
                  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `pliromata-${weekStartStr}.csv`;
                  link.click();
                }}
              >
                Εξαγωγή CSV
              </Button>
            )}
          </Box>
        </Paper>

        {/* Employees List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {filteredEmployees.map(employee => {
              const statusColor = employee.paymentStatus === 'prepaid' ? '#22c55e'
                : employee.paymentStatus === 'paid' ? '#22c55e' 
                : employee.paymentStatus === 'partial' ? '#f59e0b' : '#ef4444';
              const weekBalance = formatWeekBalanceLabel(employee.displayBalance, employee.balanceKind);
              const initials = (employee.name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              
              return (
              <Card key={employee.id} sx={{
                borderRadius: 3,
                border: 'none',
                borderLeft: `4px solid ${statusColor}`,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)' 
                },
                overflow: 'visible'
              }}>
                <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                  {/* Mobile Layout */}
                  {isMobile ? (
                    <Box>
                      {/* Row 1: Avatar + Name + Status Badge */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            size="small"
                            checked={selectedEmployees.has(employee.id)}
                            onChange={() => handleCheckboxChange(employee.id)}
                            sx={{ p: 0.25 }}
                          />
                          <Avatar sx={{ 
                            width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700,
                            bgcolor: statusColor, color: '#fff'
                          }}>
                            {initials}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="subtitle2"
                              sx={{ 
                                fontWeight: 700, lineHeight: 1.2,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                              }}
                              onClick={() => setEmployeeModal({ open: true, emp: employee, tab: 2 })}
                            >
                              {employee.name}
                            </Typography>
                            {employee.position && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {employee.position}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {getPaymentStatusBadge(employee.paymentStatus, employee.totalPaid, employee.totalSalary, employee.creditApplied)}
                      </Box>

                      {/* Row 2: Mini stat boxes */}
                      <Grid container spacing={1} sx={{ mb: 1.5 }}>
                        <Grid item xs={4}>
                          <Box sx={{ p: 1, bgcolor: 'rgba(59,130,246,0.06)', borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                              Μισθός
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                              €{(employee.totalSalary || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{ p: 1, bgcolor: 'rgba(34,197,94,0.06)', borderRadius: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                              Πληρωμένα
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#16a34a' }}>
                              €{(employee.totalPaid || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={4}>
                          <Box sx={{
                            p: 1,
                            bgcolor: employee.balanceKind === 'prepaid' ? 'rgba(34,197,94,0.08)' : employee.balanceKind === 'debt' ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.04)',
                            borderRadius: 2,
                            textAlign: 'center',
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                              {weekBalance.caption} Εβδ.
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: weekBalance.color }}>
                              {weekBalance.text}
                            </Typography>
                            {employee.cumulativeOwed > 0 && employee.cumulativeOwed !== Math.max(0, employee.displayBalance) && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', display: 'block' }}>
                                Σύνολο: €{employee.cumulativeOwed.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Row 3: Action Icons */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenPaymentModal(employee)}
                          disabled={employee.unpaidAmount <= 0}
                          sx={{ 
                            bgcolor: 'primary.main', 
                            color: 'white',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'primary.dark' },
                            '&.Mui-disabled': { bgcolor: 'grey.200', color: 'grey.400' }
                          }}
                        >
                          <PaymentIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenHistoryModal(employee)}
                          title="Ιστορικό Πληρωμών"
                          sx={{ bgcolor: 'grey.50', borderRadius: 2, '&:hover': { bgcolor: 'grey.200' } }}
                        >
                          <HistoryIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenShiftsModal(employee)}
                          title="Βάρδιες"
                          color="primary"
                          sx={{ bgcolor: 'grey.50', borderRadius: 2, '&:hover': { bgcolor: 'grey.200' } }}
                        >
                          <ScheduleIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleExpandToggle(employee.id)}
                          title={employee.cumulativeOwed > 0 ? "Δείτε ανάλυση χρέους" : "Δείτε λεπτομέρειες"}
                          sx={{ bgcolor: 'grey.50', borderRadius: 2, '&:hover': { bgcolor: 'grey.200' } }}
                        >
                          {expandedEmployees.has(employee.id) ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>

                      {/* Row 4: Full Payment Button (if applicable) */}
                      {employee.cumulativeOwed > employee.unpaidAmount && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          fullWidth
                          onClick={() => handleOpenPaymentModal({
                            ...employee,
                            unpaidAmount: employee.cumulativeOwed,
                            _payingCumulative: true
                          })}
                          sx={{ mt: 1.5, fontSize: '0.75rem', borderRadius: 2 }}
                        >
                          Πληρωμή Όλου ({employee.unpaidWeeks.length} εβδ.)
                        </Button>
                      )}
                    </Box>
                  ) : (
                    /* Desktop Layout */
                    <Box>
                      {/* Row 1: Employee Info + Stats */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Checkbox + Avatar + Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 240 }}>
                          <Checkbox
                            checked={selectedEmployees.has(employee.id)}
                            onChange={() => handleCheckboxChange(employee.id)}
                            sx={{ p: 0.5 }}
                          />
                          <Avatar sx={{ 
                            width: 44, height: 44, fontWeight: 700,
                            bgcolor: statusColor, color: '#fff',
                            fontSize: '0.95rem'
                          }}>
                            {initials}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="subtitle1"
                              sx={{ 
                                fontWeight: 700, lineHeight: 1.2,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                              }}
                              onClick={() => setEmployeeModal({ open: true, emp: employee, tab: 2 })}
                            >
                              {employee.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.position}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Status Badge */}
                        <Box sx={{ minWidth: 130 }}>
                          {getPaymentStatusBadge(employee.paymentStatus, employee.totalPaid, employee.totalSalary, employee.creditApplied)}
                        </Box>

                        {/* Mini stat boxes */}
                        <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                          <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(59,130,246,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              Μισθός
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              €{(employee.totalSalary || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(34,197,94,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 100 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              Πληρωμένα
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#16a34a' }}>
                              €{(employee.totalPaid || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          {(() => {
                            const wb = formatWeekBalanceLabel(employee.displayBalance, employee.balanceKind);
                            return (
                          <Box sx={{
                            px: 2, py: 1,
                            bgcolor: employee.balanceKind === 'prepaid' ? 'rgba(34,197,94,0.08)' : employee.balanceKind === 'debt' ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.04)',
                            borderRadius: 2, textAlign: 'center', minWidth: 100,
                          }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {wb.caption} Εβδ.
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: wb.color }}>
                                {wb.text}
                              </Typography>
                          </Box>
                            );
                          })()}
                          <Box sx={{ px: 2, py: 1, bgcolor: employee.cumulativeOwed > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 110 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                              Σύνολο Οφειλής
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: employee.cumulativeOwed > 0 ? '#dc2626' : '#16a34a' }}>
                                €{(employee.cumulativeOwed || 0).toFixed(2)}
                              </Typography>
                              {employee.cumulativeOwed > 0 && employee.unpaidWeeks.length > 1 && (
                                <Chip 
                                  label={`${employee.unpaidWeeks.length} εβδ.`} 
                                  size="small" 
                                  color="error" 
                                  sx={{ fontSize: '0.65rem', height: '18px' }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* Row 2: Actions */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'flex-start',
                        gap: 1,
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={() => handleOpenPaymentModal(employee)}
                          disabled={employee.unpaidAmount <= 0}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                          Πληρωμή
                        </Button>
                        {employee.cumulativeOwed > employee.unpaidAmount && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() => handleOpenPaymentModal({
                              ...employee,
                              unpaidAmount: employee.cumulativeOwed,
                              _payingCumulative: true
                            })}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                          >
                            Πληρωμή Όλου ({employee.unpaidWeeks.length} εβδ.)
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenHistoryModal(employee)}
                          title="Ιστορικό Πληρωμών"
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'grey.200' }
                          }}
                        >
                          <HistoryIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenShiftsModal(employee)}
                          title="Βάρδιες"
                          color="primary"
                          sx={{ 
                            bgcolor: 'rgba(0, 128, 96, 0.08)',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'rgba(0, 128, 96, 0.15)' }
                          }}
                        >
                          <ScheduleIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandToggle(employee.id)}
                          title={employee.cumulativeOwed > 0 ? "Δείτε ανάλυση χρέους" : "Δείτε λεπτομέρειες"}
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'grey.200' }
                          }}
                        >
                          {expandedEmployees.has(employee.id) ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>
                  )}

                  {/* Expanded Cumulative Debt Details */}
                  {expandedEmployees.has(employee.id) && employee.cumulativeOwed > 0 && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                        Ανάλυση Σωρευτικού Χρέους - {employee.name}
                      </Typography>
                      {employee.unpaidWeeks.length > 0 ? (
                        <TableContainer sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Εβδομάδα</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Μισθός</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Πληρωμή</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Υπόλοιπο</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ενέργειες</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {employee.unpaidWeeks.map((week, idx) => {
                                const weekStart = dayjs(week.week_start);
                                const weekEnd = weekStart.add(6, 'day');
                                const weekLabel = `${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM')}`;
                                
                                return (
                                  <TableRow key={idx}>
                                    <TableCell>{weekLabel}</TableCell>
                                    <TableCell>€{(week.salary || 0).toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                      €{(week.paid || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell>
                                      <Typography sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                        €{(week.unpaid || 0).toFixed(2)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<PaymentIcon />}
                                        onClick={() => {
                                          // Pre-fill payment for this specific week
                                          handleOpenPaymentModal({
                                            ...employee,
                                            totalSalary: week.salary,
                                            totalPaid: week.paid,
                                            unpaidAmount: week.unpaid,
                                            _selectedWeek: week.week_start
                                          });
                                        }}
                                      >
                                        Πληρωμή
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Δεν υπάρχουν βάρδιες για αυτή την εβδομάδα
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </Stack>
        )}

        {/* Payment Modal */}
        <Dialog open={paymentModal.open} onClose={handleClosePaymentModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Καταχώρηση Πληρωμής
            {paymentModal.employee && (
              <Typography variant="body2" color="textSecondary">
                {paymentModal.employee.name} - {paymentModal.employee.position}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {paymentModal.employee && (
                <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Συνολικός Μισθός:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        €{(paymentModal.employee.totalSalary || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">Ήδη Πληρωμένα:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        €{(paymentModal.employee.totalPaid || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      {(() => {
                        const wb = formatWeekBalanceLabel(
                          paymentModal.employee.displayBalance,
                          paymentModal.employee.balanceKind
                        );
                        return (
                          <>
                      <Typography variant="body2">{wb.caption} (εβδομάδα):</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: wb.color }}>
                        {wb.text}
                      </Typography>
                          </>
                        );
                      })()}
                      {(paymentModal.employee.creditApplied || 0) > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          (συμπεριλαμβάνει €{paymentModal.employee.creditApplied.toFixed(2)} από προκαταβολή)
                        </Typography>
                      )}
                    </Grid>
                    {(paymentModal.employee.creditBalance || 0) > 0.01 &&
                      paymentModal.employee.balanceKind !== 'prepaid' && (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ py: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Προκαταβολή:</strong> €{paymentModal.employee.creditBalance.toFixed(2)} — απομένει μέχρι αυτή την εβδομάδα
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {paymentModal.employee.cumulativeOwed > 0 && paymentModal.employee.cumulativeOwed !== paymentModal.employee.unpaidAmount && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Υπόλοιπο:</strong> €{(paymentModal.employee.cumulativeOwed || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="caption">
                            Από {paymentModal.employee.unpaidWeeks?.length || 0} εβδομάδες
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              <TextField
                label="Ποσό Πληρωμής (€)"
                type="number"
                value={paymentModal.amount}
                onChange={(e) => setPaymentModal({ ...paymentModal, amount: e.target.value })}
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
                <DatePicker
                  label="Ημερομηνία Πληρωμής"
                  value={paymentModal.paymentDate}
                  onChange={(newDate) => setPaymentModal({ ...paymentModal, paymentDate: newDate })}
                  format="dd/MM/yyyy"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>

              <FormControl fullWidth>
                <InputLabel>Μέθοδος Πληρωμής</InputLabel>
                <Select
                  value={paymentModal.paymentMethod}
                  label="Μέθοδος Πληρωμής"
                  onChange={(e) => setPaymentModal({ ...paymentModal, paymentMethod: e.target.value })}
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Αριθμός Αναφοράς (προαιρετικό)"
                value={paymentModal.reference}
                onChange={(e) => setPaymentModal({ ...paymentModal, reference: e.target.value })}
                fullWidth
                placeholder="π.χ. Αρ. Απόδειξης, Αρ. Συναλλαγής"
              />

              <TextField
                label="Σημειώσεις (προαιρετικό)"
                value={paymentModal.notes}
                onChange={(e) => setPaymentModal({ ...paymentModal, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              
              {/* Overpayment info */}
              {paymentModal.employee && paymentModal.employee.unpaidWeeks && (() => {
                const totalUnpaid = paymentModal.employee.unpaidWeeks.reduce((sum, w) => sum + (w.unpaid || 0), 0);
                const paymentAmount = parseFloat(paymentModal.amount) || 0;
                const overpayment = Math.max(0, paymentAmount - totalUnpaid);
                
                if (overpayment > 0) {
                  return (
                    <Alert severity="info">
                      <Typography variant="caption">
                        <strong>Προκαταβολή:</strong> Τα επιπλέον €{overpayment.toFixed(2)} θα καταχωρηθούν ως προκαταβολή 
                        για την τρέχουσα/επόμενη εβδομάδα.
                      </Typography>
                    </Alert>
                  );
                }
                return null;
              })()}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaymentModal}>Άκυρο</Button>
            <Button onClick={handleSubmitPayment} variant="contained" color="primary">
              Καταχώρηση Πληρωμής
            </Button>
          </DialogActions>
        </Dialog>

        {/* History Modal */}
        <Dialog 
          open={historyModal.open} 
          onClose={handleCloseHistoryModal} 
          maxWidth="md" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {/* Modern gradient header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            px: 3, py: 2.5, color: '#fff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <HistoryIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  Ιστορικό Πληρωμών
                </Typography>
                {historyModal.employee && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {historyModal.employee.name} — Εβδομάδα {formatDateDisplay(formatDateForAPI(selectedWeek))}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Summary mini stats */}
            {historyPayments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, backdropFilter: 'blur(10px)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: '0.65rem' }}>
                    Σύνολο Πληρωμών
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                    €{historyPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, backdropFilter: 'blur(10px)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: '0.65rem' }}>
                    Αρ. Πληρωμών
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                    {historyPayments.length}
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, backdropFilter: 'blur(10px)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: '0.65rem' }}>
                    Τελευταία
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                    {formatDateDisplay(historyPayments[0]?.payment_date)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ px: 3, pt: 2, pb: historyPayments.length > 0 ? 0 : 2 }}>
              <Typography variant="caption" color="text.secondary">
                Για διόρθωση ποσού, διαγράψτε την πληρωμή και καταχωρήστε νέα.
              </Typography>
            </Box>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : historyPayments.length > 0 ? (
              <Stack spacing={0}>
                {historyPayments.map((payment, index) => {
                  const methodConfig = {
                    cash: { label: 'Μετρητά', color: '#16a34a', bg: 'rgba(34,197,94,0.1)' },
                    bank_transfer: { label: 'Τράπεζα', color: '#2563eb', bg: 'rgba(37,99,235,0.1)' },
                    check: { label: 'Επιταγή', color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' }
                  };
                  const method = methodConfig[payment.payment_method] || methodConfig.cash;

                  return (
                    <Box 
                      key={payment.id || index}
                      sx={{ 
                        px: 3, py: 2,
                        borderBottom: index < historyPayments.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {/* Left: Amount + Method + Date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* Amount circle */}
                          <Box sx={{
                            width: 52, height: 52, borderRadius: '50%',
                            bgcolor: 'rgba(34,197,94,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#16a34a' }}>
                              €{(payment.amount || 0).toFixed(0)}
                            </Typography>
                          </Box>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                €{(payment.amount || 0).toFixed(2)}
                              </Typography>
                              <Chip 
                                label={method.label}
                                size="small"
                                sx={{ 
                                  bgcolor: method.bg, color: method.color,
                                  fontWeight: 600, fontSize: '0.7rem', height: 22,
                                  borderRadius: '6px'
                                }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                              {formatDateDisplay(payment.payment_date)}
                              {payment.reference && (
                                <Typography component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>•</Typography>
                              )}
                              {payment.reference && (
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {payment.reference}
                                </Typography>
                              )}
                            </Typography>
                            {payment.allocated_weeks && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.72rem' }}>
                                Κατανομή: {payment.allocated_weeks}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Right: Actions */}
                        <IconButton 
                          size="small"
                          onClick={() => handleDeletePayment(payment.id || payment.payment_id)}
                          sx={{ 
                            bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2,
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' }
                          }}
                        >
                          <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Δεν υπάρχουν πληρωμές
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Οι πληρωμές θα εμφανίζονται εδώ μόλις καταχωρηθούν
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              onClick={handleCloseHistoryModal}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Κλείσιμο
            </Button>
          </DialogActions>
        </Dialog>

        {/* Shifts Modal */}
        <Dialog open={shiftsModal.open} onClose={handleCloseShiftsModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              Βάρδιες Εβδομάδας
            </Box>
            {shiftsModal.employee && (
              <Typography variant="body2" color="textSecondary">
                {shiftsModal.employee.name} - {shiftsModal.employee.position}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {selectedWeek.format('DD/MM')} - {selectedWeek.add(6, 'day').format('DD/MM/YYYY')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {shiftsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {/* Bulk Apply */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="09:00"
                    sx={{ width: '80px' }}
                    inputProps={{ style: { fontSize: '0.875rem', textAlign: 'center' } }}
                    id="bulk-start-time-v2"
                  />
                  <Typography variant="body2">—</Typography>
                  <TextField
                    size="small"
                    placeholder="17:00"
                    sx={{ width: '80px' }}
                    inputProps={{ style: { fontSize: '0.875rem', textAlign: 'center' } }}
                    id="bulk-end-time-v2"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const bulkStart = document.getElementById('bulk-start-time-v2').value;
                      const bulkEnd = document.getElementById('bulk-end-time-v2').value;
                      if (bulkStart && bulkEnd) {
                        handleApplyToAllDays(bulkStart, bulkEnd);
                      }
                    }}
                  >
                    Σε Όλες
                  </Button>
                </Box>
                
                <Divider />
                
                {/* Days List */}
                {DAYS_OF_WEEK.map((day) => {
                  const dayTime = shiftsDayTimes[day.value] || { startTime: '', endTime: '' };
                  const hasShift = dayTime.startTime && dayTime.endTime;
                  
                  return (
                    <Box 
                      key={day.value}
                      sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' }, 
                        gap: 1,
                        p: 1.5,
                        border: hasShift ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        bgcolor: hasShift ? '#f0f7ff' : 'transparent'
                      }}
                    >
                      {/* Row 1: Day + Time inputs + Delete button */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Chip 
                          label={day.label} 
                          size="small"
                          color={day.value === 0 || day.value === 6 ? 'warning' : 'default'}
                          sx={{ minWidth: { xs: '75px', sm: '80px' } }}
                        />
                        <TextField
                          size="small"
                          placeholder="09:00"
                          value={dayTime.startTime}
                          onChange={(e) => handleTimeChange(day.value, 'startTime', e.target.value)}
                          inputProps={{ 
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: { 
                              fontSize: '0.9rem', 
                              textAlign: 'center',
                              fontWeight: 500,
                              letterSpacing: '1px'
                            }
                          }}
                          sx={{ 
                            width: { xs: '72px', sm: '85px' },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: '#f5f5f5' },
                              '&.Mui-focused': { 
                                bgcolor: '#fff',
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                              }
                            },
                            '& input': { 
                              p: { xs: '10px 4px', sm: '10px 8px' }
                            }
                          }}
                        />
                        <Typography sx={{ color: 'text.secondary', fontWeight: 300 }}>→</Typography>
                        <TextField
                          size="small"
                          placeholder="17:00"
                          value={dayTime.endTime}
                          onChange={(e) => handleTimeChange(day.value, 'endTime', e.target.value)}
                          inputProps={{ 
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: { 
                              fontSize: '0.9rem', 
                              textAlign: 'center',
                              fontWeight: 500,
                              letterSpacing: '1px'
                            }
                          }}
                          sx={{ 
                            width: { xs: '72px', sm: '85px' },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: '#f5f5f5' },
                              '&.Mui-focused': { 
                                bgcolor: '#fff',
                                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                              }
                            },
                            '& input': { 
                              p: { xs: '10px 4px', sm: '10px 8px' }
                            }
                          }}
                        />
                        {/* Preset hours - desktop only (inline) */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                          {presetHoursOptions.map((preset, idx) => (
                            <Chip
                              key={idx}
                              label={preset}
                              size="small"
                              variant="outlined"
                              onClick={() => handleApplyPreset(day.value, preset)}
                              sx={{ 
                                cursor: 'pointer',
                                fontSize: '0.7rem',
                                height: '26px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  borderColor: 'primary.main',
                                  transform: 'scale(1.05)'
                                }
                              }}
                            />
                          ))}
                        </Box>
                        {hasShift && (
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleClearDay(day.value)}
                            title="Καθαρισμός"
                            sx={{ 
                              ml: 'auto',
                              transition: 'all 0.2s ease',
                              '&:hover': { 
                                bgcolor: 'error.light',
                                color: 'white',
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      {/* Row 2: Preset hours - mobile only (separate row) */}
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.75, flexWrap: 'wrap', pl: 0.5, pt: 0.5 }}>
                        {presetHoursOptions.map((preset, idx) => (
                          <Chip
                            key={idx}
                            label={preset}
                            size="small"
                            variant="outlined"
                            onClick={() => handleApplyPreset(day.value, preset)}
                            sx={{ 
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              height: '28px',
                              borderRadius: '10px',
                              fontWeight: 500,
                              px: 0.5,
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main'
                              },
                              '&:active': {
                                transform: 'scale(0.95)'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseShiftsModal}>Άκυρο</Button>
            <Button 
              onClick={handleSaveAllShifts} 
              variant="contained" 
              color="success"
              disabled={updateShiftMutation.isPending || createShiftMutation.isPending || deleteShiftMutation.isPending}
            >
              {(updateShiftMutation.isPending || createShiftMutation.isPending || deleteShiftMutation.isPending) 
                ? 'Αποθήκευση...' 
                : 'Αποθήκευση Αλλαγών'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Employee Management Modal */}
        <EmployeeManagementModal
          open={employeeModal.open}
          employee={employeeModal.emp}
          tab={employeeModal.tab}
          onClose={() => {
            setEmployeeModal({ open: false, emp: null, tab: 2 });
            setHistoryEmployeeId(null);
          }}
          onTabChange={(newTab) => setEmployeeModal({ ...employeeModal, tab: newTab })}
          positions={positions}
          presetHours={presetHoursOptions}
          editForm={editForm}
          onEditFormChange={(e) => setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}
          onEditFormSet={setEditForm}
          onEditSave={handleEditSave}
          onPreferencesSave={handlePreferencesSave}
          paymentStats={modalPaymentStats}
          paymentHistory={historyPayments}
          employeeShifts={modalEmployeeShifts}
          onOpenNewPayment={() => {
            if (employeeModal.emp) {
              handleOpenPaymentModal(employeeModal.emp);
              setEmployeeModal({ open: false, emp: null, tab: 2 });
            }
          }}
          onOpenEditPayment={() => {}}
          onDeletePayment={() => {}}
          showFixedSchedule={false}
          fixedSchedule={fixedSchedule}
          onFixedScheduleChange={setFixedSchedule}
          onSaveFixedSchedule={() => {}}
          savingFixedSchedule={savingFixedSchedule}
        />
      </Container>

      <UpgradeModal
        open={exportUpgradeOpen}
        onClose={() => setExportUpgradeOpen(false)}
        featureName="Export PDF/Excel"
        message="Η εξαγωγή αναφορών είναι διαθέσιμη στα πλάνα Pro και Business."
      />
    </LocalizationProvider>
  );
};

export default PaymentManagementV2;
